<script setup lang="ts">
import type { StoreProductImage } from "@medusajs/types";
import { useProductStore } from "../lib/product-store";
import { ClientOnly } from "#components";
import { ZoomImg } from "vue3-zoomer";
import ProductImage from "~/widgets/products-grid/ui/product-image.vue";
import { useSiteSlider } from "~/shared/lib/use-site-slider";

const productStore = useProductStore();
const { product, variant } = storeToRefs(productStore);
const mainImage = ref(product?.value?.images?.[0]);
const isError = ref(!mainImage.value?.url?.trim());
const img = useImage();

const appConfig = useAppConfig();
const isS3 = computed(() => appConfig.provider === "s3");

function getThumbnailUrl(image?: StoreProductImage) {
    if (!image?.url?.trim()) return "/not_found.png";

    return image.url.replace("500px", "100px");
}

function getDefaultUrl(image?: StoreProductImage, original?: boolean) {
    if (!image?.url?.trim()) return "/not_found.png";

    return isS3.value
        ? img(
              image.url,
              original
                  ? {
                        width: 2400,
                    }
                  : {
                        width: 400,
                    },
              {
                  //@ts-ignore
                  provider: "customS3",
              },
          )
        : image.url.replace("500px", "1400px");
}

function getPreviewUrl(image?: StoreProductImage) {
    if (!image?.url?.trim()) return "/not_found.png";

    return isS3.value
        ? img(
              image.url,
              {
                  width: 800,
              },
              {
                  //@ts-ignore
                  provider: "customS3",
              },
          )
        : image.url;
}

const colorImages = computed(() =>
    product.value?.images?.filter(
        (i) =>
            //@ts-ignore
            i.metadata?.color?.toLowerCase() ===
            //@ts-ignore
            variant.value?.metadata?.color?.toLowerCase(),
    ),
);
const activeImages = computed(() => {
    if (colorImages.value && colorImages.value.length > 0) {
        return colorImages.value;
    }
    return product.value?.images ?? [];
});

const zoomImgRef = useTemplateRef("zoomImgRef");
const onErrorZoomImg = () => {
    if (!zoomImgRef.value) return;
    isError.value = true;
};
const changeMainImage = (image: StoreProductImage) => {
    mainImage.value = image;
    isError.value = false;
};

watch(
    () => variant.value?.metadata?.color,
    (colorString) => {
        if (mainImage.value?.metadata?.color !== colorString) {
            mainImage.value = activeImages.value?.[0];
        }
    },
    {
        immediate: true,
    },
);

const containerRef = ref<HTMLElement | null>(null);
const mobileImages = computed(() => activeImages.value ?? []);
const slider = useSiteSlider(containerRef);
watch(mobileImages, () => slider.update(true));
</script>
<template>
    <div>
        <div
            class="md:grid items-start grid-cols-[5.75rem_1fr] hidden gap-17.5 lg:gap-4 w-full"
        >
            <div class="h-152 overflow-y-hidden">
                <div
                    class="w-full h-full hide-scrollbar flex flex-col overflow-y-auto snap-mandatory snap-y gap-4 lg:gap-3"
                >
                    <ProductImage
                        v-for="image of activeImages"
                        :key="image.id"
                        :src="getThumbnailUrl(image)"
                        alt="Product Image"
                        :width="184"
                        loading="eager"
                        class="max-w-23 min-h-fit snap-start cursor-pointer aspect-23/28 object-cover"
                        @click="changeMainImage(image)"
                    />
                </div>
            </div>
            <div
                class="relative h-full overflow-hidden desktop-media max-w-full aspect-3/4"
            >
                <div v-if="!isError" class="absolute inset-0">
                    <ClientOnly>
                        <ZoomImg
                            :key="mainImage?.id ?? mainImage?.url"
                            ref="zoomImgRef"
                            class="h-full w-full object-cover"
                            trigger="hover"
                            :zoom-scale="3"
                            :src="getDefaultUrl(mainImage, true)"
                            @error="onErrorZoomImg"
                        >
                            <template #loading>
                                <ProductImage
                                    :src="getPreviewUrl(mainImage)"
                                    :alt="product?.title as string"
                                    class="h-full w-full object-cover"
                                />
                            </template>
                        </ZoomImg>
                        <template #fallback>
                            <ProductImage
                                :src="getPreviewUrl(mainImage)"
                                :alt="product?.title as string"
                                class="h-full w-full object-cover"
                            />
                        </template>
                    </ClientOnly>
                </div>
                <img
                    v-if="isError"
                    src="/not_found.png"
                    alt="Product Image"
                    class="absolute inset-0 h-full w-full object-cover"
                />
            </div>
        </div>

        <div class="md:hidden relative">
            <div ref="containerRef" class="keen-slider aspect-15/18">
                <div
                    v-for="(image, index) in mobileImages"
                    :key="image.id"
                    class="keen-slider__slide size-full"
                >
                    <ProductImage
                        class="object-cover object-center size-full"
                        :src="image.url"
                        :alt="product?.title as string"
                        :loading="index === 0 ? 'eager' : 'lazy'"
                    />
                </div>
            </div>
            <div
                v-if="mobileImages.length > 1"
                class="product-media-pagination z-10 absolute w-full gap-1 bottom-2 px-2 flex"
            >
                <button
                    v-for="(_, index) in mobileImages"
                    :key="index"
                    type="button"
                    class="image-pagination-bullet"
                    :class="{ 'is-active': slider.activeIndex.value === index }"
                    :aria-label="`Изображение ${index + 1}`"
                    :aria-current="slider.activeIndex.value === index ? 'true' : undefined"
                    @click="slider.to(index)"
                />
            </div>
        </div>
    </div>
</template>
<style lang="css">
.product-media-pagination .image-pagination-bullet {
    width: 100%;
    height: 2px;
    border-radius: 4px;
    background-color: rgba(255, 255, 255, 0.6);
}
.product-media-pagination .image-pagination-bullet.is-active {
    background-color: rgba(255, 255, 255, 1);
}

.desktop-media .vz-zoomimg-img {
    object-fit: cover;
}
</style>
